import { useState, useEffect, useCallback, useRef, useMemo } from 'react'

interface UseVirtualScrollOptions {
  /** 全アイテム数 */
  itemCount: number
  /** 各アイテムの高さ（px） */
  itemHeight: number
  /** 上下に余分に描画する行数 */
  overscan?: number
}

interface UseVirtualScrollResult {
  /** コンテナに設定する ref (React 18 の型では | null を付けると JSX の ref に渡せない) */
  containerRef: React.RefObject<HTMLDivElement>
  /** 全体の高さを持つ内側要素のスタイル */
  innerStyle: React.CSSProperties
  /** 表示すべきアイテムの範囲 */
  visibleRange: { start: number; end: number }
  /** 各アイテムをラップする要素のスタイル生成 */
  getItemStyle: (index: number) => React.CSSProperties
}

/**
 * 固定高さアイテム向けの軽量な仮想スクロールフック
 */
export function useVirtualScroll({
  itemCount,
  itemHeight,
  overscan = 5,
}: UseVirtualScrollOptions): UseVirtualScrollResult {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)

  // スクロールイベント
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      setScrollTop(container.scrollTop)
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height)
      }
    })

    container.addEventListener('scroll', handleScroll, { passive: true })
    observer.observe(container)
    setContainerHeight(container.clientHeight)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      observer.disconnect()
    }
  }, [])

  const totalHeight = itemCount * itemHeight

  const visibleRange = useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight)
    const endIndex = Math.min(
      itemCount,
      Math.ceil((scrollTop + containerHeight) / itemHeight)
    )
    return {
      start: Math.max(0, startIndex - overscan),
      end: Math.min(itemCount, endIndex + overscan),
    }
  }, [scrollTop, containerHeight, itemCount, itemHeight, overscan])

  const innerStyle: React.CSSProperties = useMemo(() => ({
    height: totalHeight,
    position: 'relative',
  }), [totalHeight])

  const getItemStyle = useCallback((index: number): React.CSSProperties => ({
    position: 'absolute',
    top: index * itemHeight,
    left: 0,
    right: 0,
    height: itemHeight,
  }), [itemHeight])

  return { containerRef, innerStyle, visibleRange, getItemStyle }
}
